/**
 * Internal Cotera/TipTap structured nodes are serialized to HTML with a
 * `data-type` attribute (see the `parseHTML` selectors on each extension, e.g.
 * `span[data-type="mention"]`). These must survive a paste untouched so that
 * copying an @ reference / mention / resource chip from one Cotera editor into
 * another keeps the rich reference instead of degrading to plain text.
 *
 * Anything else (text copied from Word, Google Docs, Slack, a browser, …) is
 * "external" HTML and should be normalized to plain text so the source app's
 * fonts, colors, and spacing do not leak into the editor.
 *
 * Keep this list in sync with the `data-type` values used by the extensions in
 * `apps/client/src/app/components/ui/tiptap/extensions`.
 */
const INTERNAL_NODE_DATA_TYPES = [
  'mention',
  'emoji',
  'disclaimer',
  'json-type-builder',
  'attachment',
  'expression',
] as const;

const INTERNAL_NODE_SELECTOR = INTERNAL_NODE_DATA_TYPES.map(
  (type) => `[data-type="${type}"]`
).join(',');

/**
 * Attribute the chat message Copy button stamps on its HTML clipboard payload
 * (see `serializeEditorStateForClipboard`). Copying a Coco message and pasting
 * it back into a composer is an internal round-trip: the HTML is our own clean
 * rich content (lists, marks, …), so it must be preserved rather than flattened
 * to plain text the way genuinely external (Word/Docs/browser) HTML is.
 */
export const COTERA_RICH_CLIPBOARD_ATTR = 'data-cotera-rich-clipboard';

/** Block-level tags whose boundaries we render as line breaks when flattening. */
const BLOCK_TAGS = new Set([
  'ADDRESS',
  'ARTICLE',
  'ASIDE',
  'BLOCKQUOTE',
  'DD',
  'DIV',
  'DL',
  'DT',
  'FIGCAPTION',
  'FIGURE',
  'FOOTER',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'HEADER',
  'HR',
  'LI',
  'MAIN',
  'NAV',
  'OL',
  'P',
  'PRE',
  'SECTION',
  'TABLE',
  'TR',
  'UL',
]);

const escapeHtml = (input: string): string =>
  input.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * True when the pasted HTML contains a Cotera/TipTap structured node (mention,
 * emoji, attachment, …). Such HTML is "internal" and must be parsed normally so
 * the structured node survives.
 */
export function htmlContainsInternalNode(html: string): boolean {
  if (typeof DOMParser === 'undefined') {
    return false;
  }
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.querySelector(INTERNAL_NODE_SELECTOR) !== null;
}

/** Flatten a DOM subtree to text, inserting newlines at block/`<br>` boundaries. */
function flattenToText(node: Node): string {
  const parts: string[] = [];
  const visit = (current: Node): void => {
    current.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        parts.push(child.textContent ?? '');
        return;
      }
      if (child.nodeType !== Node.ELEMENT_NODE) {
        return;
      }
      const element = child as Element;
      const tag = element.tagName;
      if (tag === 'BR') {
        parts.push('\n');
        return;
      }
      if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'HEAD') {
        return;
      }
      const isBlock = BLOCK_TAGS.has(tag);
      if (isBlock) {
        parts.push('\n');
      }
      visit(element);
      if (isBlock) {
        parts.push('\n');
      }
    });
  };
  visit(node);
  return parts.join('');
}

/**
 * Strip all formatting from external HTML, returning HTML that renders the same
 * text as plain paragraphs (double newline → new paragraph, single newline →
 * `<br>`). Returns `''` when there is no textual content.
 */
function plainTextHtmlFromExternalHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const text = flattenToText(doc.body)
    // Collapse runs of blank lines produced by nested block elements.
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  if (text === '') {
    return '';
  }

  return text
    .split(/\n{2,}/)
    .map(
      (paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, '<br>')}</p>`
    )
    .join('');
}

/**
 * `editorProps.transformPastedHTML` hook: normalize external rich HTML to plain
 * text while leaving internal Cotera structured content (mentions, references,
 * emoji, …) untouched so it parses normally.
 */
export function transformExternalPastedHtml(html: string): string {
  if (html.trim() === '') {
    return html;
  }
  if (typeof DOMParser === 'undefined') {
    return html;
  }
  // Content copied from a Coco message (via the message Copy button) is internal
  // rich content — preserve it verbatim so lists / marks round-trip instead of
  // being flattened to plain paragraphs.
  if (html.includes(COTERA_RICH_CLIPBOARD_ATTR)) {
    return html;
  }
  if (htmlContainsInternalNode(html)) {
    return html;
  }
  return plainTextHtmlFromExternalHtml(html);
}
